import React, { useState, useRef } from 'react';
import client from '../../../api/client';

const SPIN = (
  <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
  </svg>
);

function Chip({ label }) {
  return (
    <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-brand-50 border border-brand-100 text-[11px] font-mono text-brand-600">
      {label}
    </span>
  );
}

function DropZone({ file, dragging, inputRef, onDragOver, onDragLeave, onDrop, onClick, prompt }) {
  return (
    <div
      onDragOver={onDragOver} onDragLeave={onDragLeave} onDrop={onDrop} onClick={onClick}
      className={`border-2 border-dashed rounded-xl p-5 flex items-center gap-4 cursor-pointer transition-all ${
        dragging ? 'border-brand-400 bg-brand-50/60' :
        file     ? 'border-green-400 bg-green-50/50' :
                   'border-gray-200 hover:border-brand-300 hover:bg-brand-50/30'
      }`}
    >
      <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
        onChange={(e) => onClick._handleFile?.(e.target.files[0])} />
      {file ? (
        <>
          <div className="w-9 h-9 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"/>
            </svg>
          </div>
          <div className="min-w-0">
            <p className="text-sm font-semibold text-green-700 truncate">{file.name}</p>
            <p className="text-xs text-gray-400">Click to choose a different file</p>
          </div>
        </>
      ) : (
        <>
          <div className="w-9 h-9 rounded-lg bg-brand-50 border border-brand-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-brand-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
            </svg>
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-700">{prompt}</p>
            <p className="text-xs text-gray-400">Drag & drop or click to browse — .xlsx only</p>
          </div>
        </>
      )}
    </div>
  );
}

function ErrorBanner({ msg }) {
  if (!msg) return null;
  return (
    <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-lg px-3 py-2.5 text-sm text-red-700">
      <svg className="w-4 h-4 text-red-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
      </svg>
      <span>{msg}</span>
    </div>
  );
}

function ResultBanner({ children }) {
  return (
    <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 space-y-3">
      <div className="flex items-center gap-2">
        <svg className="w-4 h-4 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
        </svg>
        <span className="text-sm font-bold text-emerald-700">Import complete</span>
      </div>
      {children}
    </div>
  );
}

function StatRow({ stats }) {
  return (
    <div className="flex gap-4 flex-wrap">
      {stats.map(({ label, value, color }) => (
        <div key={label} className="bg-white rounded-lg border border-emerald-200 px-4 py-2.5 min-w-[80px]">
          <p className={`text-xl font-black ${color}`}>{value}</p>
          <p className="text-[11px] text-gray-400 font-medium">{label}</p>
        </div>
      ))}
    </div>
  );
}

function Warnings({ errors }) {
  if (!errors?.length) return null;
  return (
    <details className="bg-amber-50 border border-amber-200 rounded-lg">
      <summary className="px-3 py-2 text-xs font-semibold text-amber-700 cursor-pointer">
        ⚠ {errors.length} warning{errors.length > 1 ? 's' : ''} — click to expand
      </summary>
      <ul className="px-3 pb-2.5 space-y-0.5 mt-1">
        {errors.map((e, i) => <li key={i} className="text-[11px] text-amber-800 font-mono">{e}</li>)}
      </ul>
    </details>
  );
}

function ConfirmFlushModal({ fileName, onConfirm, onCancel }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                d="M12 9v2m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/>
            </svg>
          </div>
          <div>
            <h3 className="text-base font-bold text-gray-900">Update inventory data?</h3>
            <p className="text-sm text-gray-500 mt-0.5">Existing data is kept.</p>
          </div>
        </div>
        <p className="text-sm text-gray-600">
          All existing <span className="font-semibold">Locations, Rooms, and Devices</span> will be updated and new ones added from <span className="font-mono text-brand-600">{fileName}</span>.
        </p>
        <p className="text-xs bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-amber-700">
          Devices are matched by Room ID_Part Serial No. Nothing is deleted and existing tickets stay linked.
        </p>
        <div className="flex gap-3 pt-1">
          <button onClick={onCancel}
            className="flex-1 px-4 py-2.5 text-sm rounded-xl border border-gray-200 text-gray-700 font-semibold hover:bg-gray-50">
            Cancel
          </button>
          <button onClick={onConfirm}
            className="flex-1 px-4 py-2.5 text-sm rounded-xl bg-blue-600 hover:bg-blue-700 text-white font-semibold">
            Yes, Upload
          </button>
        </div>
      </div>
    </div>
  );
}

function ImportCard({ icon, iconBg, accent, title, description, columns, uploadLabel, buttonLabel, endpoint, onConfirmNeeded, resultRenderer }) {
  const [file, setFile] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const inputRef = useRef();

  const handleFile = (f) => {
    if (!f) return;
    if (!f.name.match(/\.xlsx?$/i)) { setError('Please upload an .xlsx file.'); return; }
    setFile(f); setResult(null); setError(null);
  };

  const clickHandler = () => inputRef.current?.click();
  clickHandler._handleFile = handleFile;

  const doUpload = async () => {
    setLoading(true); setError(null); setResult(null);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const { data } = await client.post(endpoint, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setResult(data);
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
    } catch (err) {
      setError(err.response?.data?.error || 'Upload failed. Please check the file format.');
    } finally {
      setLoading(false);
    }
  };

  const handleClick = () => {
    if (!file) return;
    if (onConfirmNeeded) { onConfirmNeeded(file.name, doUpload); return; }
    doUpload();
  };

  return (
    <div className={`bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden`}>
      <div className={`h-1 ${accent}`} />
      <div className="p-5 space-y-4">
        {/* Header */}
        <div className="flex items-start gap-3">
          <div className={`w-10 h-10 rounded-xl ${iconBg} flex items-center justify-center flex-shrink-0`}>
            {icon}
          </div>
          <div className="min-w-0">
            <h3 className="text-base font-bold text-gray-900">{title}</h3>
            <p className="text-xs text-gray-400 mt-0.5 leading-relaxed">{description}</p>
          </div>
        </div>

        {/* Column chips */}
        <div>
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wide mb-2">Required columns</p>
          <div className="flex flex-wrap gap-1.5">
            {columns.map(c => <Chip key={c} label={c} />)}
          </div>
        </div>

        {/* Drop zone */}
        <DropZone
          file={file} dragging={dragging} inputRef={inputRef}
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); handleFile(e.dataTransfer.files[0]); }}
          onClick={clickHandler}
          prompt={uploadLabel}
        />
        <input ref={inputRef} type="file" accept=".xlsx,.xls" className="hidden"
          onChange={(e) => handleFile(e.target.files[0])} />

        <ErrorBanner msg={error} />

        <button onClick={handleClick} disabled={!file || loading}
          className="btn-primary w-full py-2.5 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2 text-sm font-semibold">
          {loading ? <>{SPIN} Importing…</> : buttonLabel}
        </button>

        {result && (
          <ResultBanner>
            {resultRenderer(result)}
          </ResultBanner>
        )}
      </div>
    </div>
  );
}

export default function DataImport() {
  const [confirmState, setConfirmState] = useState(null); // { fileName, onConfirm }

  return (
    <div className="space-y-5 max-w-2xl">
      {confirmState && (
        <ConfirmFlushModal
          fileName={confirmState.fileName}
          onConfirm={() => { confirmState.onConfirm(); setConfirmState(null); }}
          onCancel={() => setConfirmState(null)}
        />
      )}

      <div>
        <h2 className="text-xl font-bold text-gray-900">Data Import</h2>
        <p className="text-sm text-gray-400 mt-0.5">Upload Excel files to populate or update system data.</p>
      </div>

      {/* Inventory */}
      <ImportCard
        accent="bg-brand-500"
        iconBg="bg-brand-50"
        icon={
          <svg className="w-5 h-5 text-brand-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"/>
          </svg>
        }
        title="BHEL Inventory Import"
        description="Replace all Locations, Rooms, and Devices in one step. Existing data will be permanently cleared."
        columns={['Installation Date', 'OEM', 'Part ID', 'Part Serial No.', 'Room ID', 'Location Class', 'Solution Type', 'Building Name', 'Room Name', 'Unit', 'Sub Unit', 'State', 'Contract Start Date']}
        uploadLabel="Drop your BHEL Inventory Excel here"
        buttonLabel="Import Inventory"
        endpoint="/admin/upload/inventory-full/"
        onConfirmNeeded={(fileName, onConfirm) => setConfirmState({ fileName, onConfirm })}
        resultRenderer={(r) => (
          <>
            <div className="flex gap-3 flex-wrap">
              {[
                { label: 'Locations Created', value: r.locations?.created, color: 'text-green-600' },
                { label: 'Locations Updated', value: r.locations?.updated, color: 'text-brand-500' },
                { label: 'Rooms Created', value: r.rooms?.created, color: 'text-green-600' },
                { label: 'Rooms Updated', value: r.rooms?.updated, color: 'text-brand-500' },
                { label: 'Devices Created', value: r.devices?.created, color: 'text-green-600' },
                { label: 'Devices Updated', value: r.devices?.updated, color: 'text-brand-500' },
                { label: 'Devices Skipped', value: r.devices?.skipped, color: 'text-red-500' },
                { label: 'Duplicate Keys In File', value: r.devices?.duplicates_in_file, color: 'text-amber-500' },
              ].filter(s => s.value > 0).map(s => (
                <div key={s.label} className="bg-white rounded-lg border border-emerald-200 px-4 py-2.5">
                  <p className={`text-xl font-black ${s.color}`}>{s.value}</p>
                  <p className="text-[11px] text-gray-400 font-medium">{s.label}</p>
                </div>
              ))}
            </div>
            <Warnings errors={r.errors} />
          </>
        )}
      />

      {/* Spares */}
      <ImportCard
        accent="bg-amber-400"
        iconBg="bg-amber-50"
        icon={
          <svg className="w-5 h-5 text-amber-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"/>
          </svg>
        }
        title="Spare Parts Upload"
        description="Load spare units into the warehouse. Serialized items are upserted by serial; non-serialized are skipped if stock already meets quantity."
        columns={['Spare Item', 'OEM / Make', 'Spare Part Code', 'Serial Number', 'Quantity', 'Warehouse Location']}
        uploadLabel="Drop your BHEL Spares Excel here"
        buttonLabel="Import Spares"
        endpoint="/admin/upload/bhel-spares/"
        resultRenderer={(r) => (
          <>
            <StatRow stats={[
              { label: 'Created', value: r.created, color: 'text-green-600' },
              { label: 'Updated', value: r.updated ?? 0, color: 'text-blue-500' },
              { label: 'Skipped', value: r.skipped, color: 'text-amber-500' },
            ]} />
            {r.skipped_details?.length > 0 && (
              <details className="bg-amber-50 border border-amber-200 rounded-lg">
                <summary className="px-3 py-2 text-xs font-semibold text-amber-700 cursor-pointer">
                  Why were {r.skipped} skipped? — click to expand
                </summary>
                <ul className="px-3 pb-2.5 space-y-1 mt-1">
                  {r.skipped_details.map((d, i) => <li key={i} className="text-[11px] text-amber-800">{d}</li>)}
                </ul>
              </details>
            )}
            <Warnings errors={r.errors} />
          </>
        )}
      />

      {/* Contacts */}
      <ImportCard
        accent="bg-emerald-500"
        iconBg="bg-emerald-50"
        icon={
          <svg className="w-5 h-5 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z"/>
          </svg>
        }
        title="BHEL Contact & Address Import"
        description="Update SPOC contact details and site address for each location. Accepts the BHEL Contact Details format directly — no column renaming needed."
        columns={['Location ID', 'Contact Person Name', 'Contact Number', 'Email-1', 'Address Line-1', 'Pin Code', 'gstin (optional)']}
        uploadLabel="Drop your Contact Details Excel here"
        buttonLabel="Import Contacts"
        endpoint="/admin/upload/location-contacts/"
        resultRenderer={(r) => (
          <>
            <StatRow stats={[
              { label: 'Updated', value: r.updated, color: 'text-green-600' },
              { label: 'Skipped', value: r.skipped, color: 'text-amber-500' },
            ]} />
            <Warnings errors={r.errors} />
          </>
        )}
      />

      {/* OEM Escalation Contacts */}
      <ImportCard
        accent="bg-red-500"
        iconBg="bg-red-50"
        icon={
          <svg className="w-5 h-5 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
              d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"/>
          </svg>
        }
        title="OEM / Vendor Escalation Contacts"
        description="Upload vendor warranty details. Each row creates up to 3 escalation levels (Primary → L1, Escalation Level 1 → L2, Final Escalation → L3)."
        columns={['Product Make', 'Primary Contact Name', 'Primary Contact E Mail ID', 'Primary Contact Number', 'Escalation Level 1 Contact Name', 'Escalation Level 1 E Mail ID', 'Final Escalation Name', 'Final Escalation E Mail ID']}
        uploadLabel="Drop your Vendor Warranty Excel here"
        buttonLabel="Import Escalation Contacts"
        endpoint="/admin/upload/bhel-escalations/"
        resultRenderer={(r) => (
          <>
            <StatRow stats={[
              { label: 'Created', value: r.created, color: 'text-green-600' },
              { label: 'Updated', value: r.updated, color: 'text-brand-500' },
              { label: 'Skipped', value: r.skipped, color: 'text-amber-500' },
            ]} />
            <Warnings errors={r.errors} />
          </>
        )}
      />
    </div>
  );
}
